// Copyright (c) Ping. All rights reserved.
//
// Tiny observable set of conversation ids (hex) the SDK reports as STRANDED — a
// conversation that missed a Commit and can't advance its local MLS epoch, so
// it can neither send nor decrypt new messages ("the encryption broke"). Fed by
// `commitPendingProposalsAndSurfaceStranded` on every sync tick; read by the
// chat room to show the "Restart secure session" banner (iOS parity — previously
// web only `log.warn`'d this, a silent black hole).

const stranded = new Set<string>();
const subscribers = new Set<(ids: ReadonlySet<string>) => void>();

function emit(): void {
  const snapshot = new Set(stranded);
  for (const cb of subscribers) cb(snapshot);
}

/** Replace the stranded set with the latest poll result. */
export function setStrandedConversations(ids: readonly string[]): void {
  const next = new Set(ids);
  if (next.size === stranded.size && [...next].every((id) => stranded.has(id))) return;
  stranded.clear();
  for (const id of next) stranded.add(id);
  emit();
}

/** Clear one conversation from the stranded set (after a repair attempt). */
export function clearStrandedConversation(id: string): void {
  if (stranded.delete(id)) emit();
}

export function isConversationStranded(id: string): boolean {
  return stranded.has(id);
}

export function subscribeStrandedConversations(
  cb: (ids: ReadonlySet<string>) => void,
): () => void {
  subscribers.add(cb);
  cb(new Set(stranded));
  return () => {
    subscribers.delete(cb);
  };
}
